import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X } from "lucide-react";

// Cross-page guided tour. Start it by dispatching: window.dispatchEvent(new Event("start-tour"))
const STEPS = [
  { route: "/dashboard", sel: '[data-testid="dashboard-page"]', title: "Welcome to Carat Ledger", text: "This quick tour shows you around. Your dashboard summarises stock value, sales, live Catawiki auctions and anything that needs attention. Use Next / Back — or Skip anytime." },
  { route: "/dashboard", sel: "aside", title: "The sidebar is your map", text: "Everything is grouped by task: Inventory, Workshop, Purchasing, Sales, Catawiki and Business. Administration stays at the bottom." },
  { route: "/dashboard", sel: '[data-testid="global-search-input"]', title: "Find anything instantly", text: "Search any stone, piece, SKU or contact from anywhere (⌘K / Ctrl-K). Tip: scan a barcode into a search box to jump straight to that piece." },
  { route: "/certified", sel: '[data-testid="certified-page"]', title: "Certified diamonds", text: "Stones tracked one-by-one with their certificate and 4Cs. Use the printer icon on any row to print its GBS label, or the eye icon for a quick preview." },
  { route: "/jewelry-stock", sel: '[data-testid="jewelry-stock-page"]', title: "Jewelry Stock", text: "Every finished piece with photos, costs and status. Click a row to open its detail — from there you print labels, upload photos, move pieces between stocks and share the wholesale catalogue." },
  { route: "/stocktake", sel: '[data-testid="stocktake-page"]', title: "Stock Take", text: "Verify what's physically in the safe: load your live stock (or import a workbook), scan every barcode, then Commit. Pieces you can't find can be marked sold." },
  { route: "/inquiries", sel: '[data-testid="inquiries-page"]', title: "Inquiries & offers", text: "Customer price requests from your shared catalogue arrive here. Set offer prices, email the offer from your own mailbox, and convert accepted offers into sales — shortfalls become backorders automatically." },
  { route: "/catawiki/shipping", sel: '[data-testid="catawiki-shipping-page"]', title: "Catawiki shipping", text: "Import the Catawiki orders file, export a UPS CSV for EU orders, scan tracking codes to mark shipped — and use Swap if a piece can't be found or the buyer changes their mind." },
  { route: "/dashboard", sel: '[data-testid="advisor-fab"]', title: "Your AI assistant", text: "Ask anything — \"how do I mark an order shipped?\" or \"which categories sell best?\". It knows the whole app manual and your live business numbers." },
  { route: "/dashboard", sel: '[data-testid="help-shortcut-btn"]', title: "Help is one click away", text: "This ? always opens the step-by-step guide for whichever page you're on. That's the tour — you can restart it anytime from the Help Center. Enjoy!" },
];

export const GuidedTour = () => {
  const navigate = useNavigate();
  const [idx, setIdx] = useState(-1);
  const [rect, setRect] = useState(null);
  const findTimer = useRef(null);

  const locate = useCallback((i) => {
    clearInterval(findTimer.current);
    let tries = 0;
    findTimer.current = setInterval(() => {
      const el = document.querySelector(STEPS[i].sel);
      tries += 1;
      if (el) {
        clearInterval(findTimer.current);
        el.scrollIntoView({ block: "nearest" });
        const r = el.getBoundingClientRect();
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      } else if (tries > 12) {
        clearInterval(findTimer.current);
        setRect(null); // element unavailable (permissions?) — show centered card
      }
    }, 250);
  }, []);

  const goto = useCallback((i) => {
    if (i < 0 || i >= STEPS.length) {
      clearInterval(findTimer.current);
      setIdx(-1); setRect(null);
      localStorage.setItem("tour_done", "1");
      return;
    }
    setIdx(i); setRect(null);
    if (window.location.pathname !== STEPS[i].route) navigate(STEPS[i].route);
    setTimeout(() => locate(i), 350);
  }, [navigate, locate]);

  useEffect(() => {
    const start = () => goto(0);
    window.addEventListener("start-tour", start);
    return () => { window.removeEventListener("start-tour", start); clearInterval(findTimer.current); };
  }, [goto]);

  useEffect(() => {
    if (idx < 0) return;
    const onKey = (e) => { if (e.key === "Escape") goto(-1); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [idx, goto]);

  if (idx < 0) return null;
  const step = STEPS[idx];
  const pad = 8;
  // tooltip placement: below the target unless it would overflow, then above/centered
  let tipStyle = { top: "50%", left: "50%", transform: "translate(-50%, -50%)" };
  if (rect) {
    const below = rect.top + rect.height + pad + 190 < window.innerHeight;
    const top = below ? rect.top + rect.height + pad + 4 : Math.max(16, rect.top - 200);
    const left = Math.min(Math.max(16, rect.left), window.innerWidth - 400);
    tipStyle = { top, left, transform: "none" };
  }
  return (
    <div className="fixed inset-0 z-[90]" data-testid="guided-tour">
      <div className="absolute inset-0 bg-black/60" onClick={() => goto(-1)} />
      {rect && (
        <div className="absolute border-2 border-[#7DD3FC] shadow-[0_0_0_9999px_rgba(0,0,0,0.6)] pointer-events-none transition-all duration-300"
          style={{ top: rect.top - pad, left: rect.left - pad, width: rect.width + pad * 2, height: rect.height + pad * 2 }} />
      )}
      <div className="absolute w-[min(380px,90vw)] bg-[#111520] border border-[#7DD3FC]/50 shadow-2xl p-5 transition-all duration-300" style={tipStyle} data-testid="tour-card">
        <div className="flex items-start justify-between gap-3">
          <p className="font-overline text-[#7DD3FC] text-[10px]">Step {idx + 1} of {STEPS.length}</p>
          <button onClick={() => goto(-1)} data-testid="tour-skip" className="text-slate-500 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        <h3 className="text-white font-semibold mt-1">{step.title}</h3>
        <p className="text-sm text-slate-300 leading-relaxed mt-2">{step.text}</p>
        <div className="flex items-center justify-between mt-4">
          <button onClick={() => goto(-1)} className="text-xs text-slate-500 hover:text-slate-300" data-testid="tour-skip-text">Skip tour</button>
          <div className="flex gap-2">
            {idx > 0 && (
              <button onClick={() => goto(idx - 1)} data-testid="tour-back"
                className="h-9 px-4 text-sm border border-[#1E293B] text-slate-300 hover:bg-[#1A202E]">Back</button>
            )}
            <button onClick={() => goto(idx + 1)} data-testid="tour-next"
              className="h-9 px-4 text-sm bg-[#7DD3FC] text-[#0A0D14] font-semibold hover:bg-[#38BDF8]">
              {idx === STEPS.length - 1 ? "Finish" : "Next"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
